import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput, validatePhone } from "@/components/ui/phone-input";
import AvatarPicker from "@/components/AvatarPicker";
import GiftGuidanceForm, { type GiftPreferences } from "@/components/card/GiftGuidanceForm";
import { Loader2, Sun, Moon, Globe, Check } from "lucide-react";

const Profile = () => {
  const { user, profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const { resolvedTheme, setTheme } = useTheme();

  const currentLang = i18n.language === "ar" ? "ar" : "en";
  const changeLanguage = (lang: "en" | "ar") => {
    i18n.changeLanguage(lang);
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  };

  const activeTab = searchParams.get("tab") || "profile";

  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [phone, setPhone] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const [giftPrefs, setGiftPrefs] = useState<GiftPreferences>({
    wants: "",
    has: "",
    style: "",
    registries: [],
    priceGuidance: "",
    showInInvite: true,
  });

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data) {
        setDisplayName(data.display_name || "");
        setAvatarUrl(
          data.avatar_url ||
            `https://api.dicebear.com/9.x/dylan/svg?seed=${encodeURIComponent(user.email || user.id)}`
        );
        setPhone(data.phone || "");
        setAge(data.age != null ? String(data.age) : "");
        setGender(data.gender || "");
        setGiftPrefs({
          wants: (data as any).preferences_wants || "",
          has: (data as any).preferences_has || "",
          style: (data as any).preferences_style || "",
          registries: ((data as any).preferences_registries as string[]) || [],
          priceGuidance: (data as any).preferences_price_guidance || "",
          showInInvite: true,
        });
      }
      setLoaded(true);
    };
    load();
  }, [user]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ext || !["jpg", "jpeg", "png", "webp"].includes(ext)) {
      toast({ variant: "destructive", title: "Invalid file type", description: "Please use JPG, PNG, or WebP." });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ variant: "destructive", title: "File too large", description: "Maximum size is 5 MB." });
      return;
    }
    setUploading(true);
    const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("avatars").upload(path, file);
    if (error) {
      toast({ variant: "destructive", title: "Upload failed", description: error.message });
      setUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);
    setAvatarUrl(urlData.publicUrl);
    setUploading(false);
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    const phoneErr = validatePhone(phone);
    if (phoneErr) { setPhoneError(phoneErr); return; }
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        display_name: displayName.trim() || null,
        avatar_url: avatarUrl || null,
        phone: phone.trim() || null,
        age: age ? parseInt(age, 10) : null,
        gender: gender || null,
      } as any)
      .eq("user_id", user.id);
    if (error) {
      toast({ variant: "destructive", title: "Error", description: "Could not save profile." });
    } else {
      toast({ title: "Saved!", description: "Your profile has been updated." });
    }
    setSaving(false);
  };

  const handleSaveGiftPrefs = async () => {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        preferences_wants: giftPrefs.wants || null,
        preferences_has: giftPrefs.has || null,
        preferences_style: giftPrefs.style || null,
        preferences_registries: giftPrefs.registries.filter(Boolean) as any,
        preferences_price_guidance: giftPrefs.priceGuidance || null,
      } as any)
      .eq("user_id", user.id);
    if (error) {
      toast({ variant: "destructive", title: "Error", description: "Could not save preferences." });
    } else {
      toast({ title: "Saved!", description: "Your gift preferences have been updated." });
    }
    setSaving(false);
  };

  if (authLoading || !loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin text-muted-foreground" size={24} />
      </div>
    );
  }

  const currentAvatar =
    avatarUrl ||
    `https://api.dicebear.com/9.x/dylan/svg?seed=${encodeURIComponent(user?.email || user?.id || "")}`;
  const displayNameForGift = profile?.display_name || user?.email || "you";

  return (
    <div className="min-h-screen bg-background">
      <AppHeader onBack="/dashboard" userMenuVariant="dashboard" />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-2xl font-bold text-foreground font-serif mb-6">
          {t("profile.title", { defaultValue: "My Profile" })}
        </h1>

        <div className="flex gap-1 mb-8 bg-muted/50 rounded-full p-1 w-fit">
          <button
            onClick={() => setSearchParams({ tab: "profile" }, { replace: true })}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
              activeTab === "profile"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t("profile.tabProfile", { defaultValue: "Profile" })}
          </button>
          <button
            onClick={() => setSearchParams({ tab: "gift-preferences" }, { replace: true })}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
              activeTab === "gift-preferences"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t("profile.tabGiftPrefs", { defaultValue: "Gift Preferences" })}
          </button>
          <button
            onClick={() => setSearchParams({ tab: "preferences" }, { replace: true })}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
              activeTab === "preferences"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t("profile.tabPreferences", { defaultValue: "Settings" })}
          </button>
        </div>

        {activeTab === "profile" && (
          <div className="space-y-6">
            <AvatarPicker
              currentUrl={currentAvatar}
              uploading={uploading}
              onSelectPreset={(url) => setAvatarUrl(url)}
              onUploadFile={(file) => {
                handleAvatarUpload({ target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>);
              }}
            />

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-foreground">
                {t("profile.displayName", { defaultValue: "Display name" })}
              </Label>
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Your name" className="border-border" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-foreground">
                {t("profile.email", { defaultValue: "Email" })}
              </Label>
              <Input value={user?.email || ""} disabled className="border-border bg-muted/50 text-muted-foreground" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-foreground">
                {t("profile.phone", { defaultValue: "Mobile number" })}
              </Label>
              <PhoneInput
                value={phone}
                onChange={(val) => { setPhone(val); setPhoneError(validatePhone(val)); }}
                error={phoneError || undefined}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-foreground">
                {t("profile.age", { defaultValue: "Age" })}
              </Label>
              <Input value={age} onChange={(e) => setAge(e.target.value.replace(/\D/g, ""))} placeholder="25" type="number" min={1} max={150} className="border-border" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-foreground">
                {t("profile.gender", { defaultValue: "Gender" })}
              </Label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm"
              >
                <option value="">{t("profile.genderPlaceholder", { defaultValue: "Select gender" })}</option>
                <option value="male">{t("profile.male", { defaultValue: "Male" })}</option>
                <option value="female">{t("profile.female", { defaultValue: "Female" })}</option>
                <option value="other">{t("profile.other", { defaultValue: "Other" })}</option>
                <option value="prefer_not_to_say">{t("profile.preferNotToSay", { defaultValue: "Prefer not to say" })}</option>
              </select>
            </div>

            <Button onClick={handleSaveProfile} disabled={saving} size="lg" className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              {t("profile.save", { defaultValue: "Save Profile" })}
            </Button>
          </div>
        )}

        {activeTab === "gift-preferences" && (
          <div>
            <p className="text-sm text-muted-foreground mb-6">
              {t("profile.giftPrefsDesc", { defaultValue: "Save your preferences here so they can be used when someone creates an event for you." })}
            </p>
            <GiftGuidanceForm
              recipientName={displayNameForGift}
              occasion="General"
              preferences={giftPrefs}
              onChange={setGiftPrefs}
              hideAiSuggestions
              profileMode
            />
            <Button onClick={handleSaveGiftPrefs} disabled={saving} size="lg" className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 mt-6">
              {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              {t("profile.savePrefs", { defaultValue: "Save Preferences" })}
            </Button>
          </div>
        )}

        {activeTab === "preferences" && (
          <div className="space-y-8">
            {/* Language — moved here from the user dropdown menu so
                all personal settings live in one place. */}
            <section>
              <div className="mb-3 flex items-center gap-2">
                <Globe size={16} className="text-muted-foreground" />
                <h2 className="text-sm font-semibold text-foreground">
                  {t("profile.language", { defaultValue: "Language" })}
                </h2>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "en" as const, label: "English" },
                  { key: "ar" as const, label: "عربي" },
                ].map((opt) => {
                  const active = currentLang === opt.key;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => changeLanguage(opt.key)}
                      className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                        active
                          ? "border-primary bg-primary/5 text-primary"
                          : "border-border text-foreground hover:border-primary/40"
                      }`}
                    >
                      <span>{opt.label}</span>
                      {active && <Check size={16} className="text-primary" />}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Theme — same rationale as language. */}
            <section>
              <div className="mb-3 flex items-center gap-2">
                <Sun size={16} className="text-muted-foreground" />
                <h2 className="text-sm font-semibold text-foreground">
                  {t("profile.theme", { defaultValue: "Theme" })}
                </h2>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "light" as const, label: t("profile.lightMode", { defaultValue: "Light" }), icon: Sun },
                  { key: "dark" as const, label: t("profile.darkMode", { defaultValue: "Dark" }), icon: Moon },
                ].map((opt) => {
                  const active = resolvedTheme === opt.key;
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => setTheme(opt.key)}
                      className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                        active
                          ? "border-primary bg-primary/5 text-primary"
                          : "border-border text-foreground hover:border-primary/40"
                      }`}
                    >
                      <Icon size={16} />
                      <span className="flex-1 text-left">{opt.label}</span>
                      {active && <Check size={16} className="text-primary" />}
                    </button>
                  );
                })}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
